import React, { useState, useEffect } from 'react';
import { Search, TrendingUp, Star, Copy, Check, Loader2, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getStores, getTrendingCoupons, getFeaturedCoupons, getSearchSuggestions } from '../services/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { toast } from '../hooks/use-toast';

const HomePage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [copiedCode, setCopiedCode] = useState(null);
  const [stores, setStores] = useState([]);
  const [featuredStores, setFeaturedStores] = useState([]);
  const [featuredCoupons, setFeaturedCoupons] = useState([]);
  const [trendingCoupons, setTrendingCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(false);
    try {
      const [allStores, trending, featured] = await Promise.all([
        getStores(),
        getTrendingCoupons(6),
        getFeaturedCoupons(6)
      ]);
      
      // Check if we got data
      if (allStores.length === 0 && trending.length === 0) {
        setError(true);
      } else {
        setStores(allStores);
        setFeaturedStores(allStores.filter(store => store.featured).slice(0, 8));
        setTrendingCoupons(trending);
        setFeaturedCoupons(featured);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const trendingSearches = [
    'Nike coupons',
    'Amazon promo codes',
    'Target deals',
    'Adidas discount',
    'Walmart savings',
    'Best Buy codes'
  ];

  const handleCopyCode = (code, couponId, storeDomain) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(couponId);
    toast({
      title: "Code Copied!",
      description: `Coupon code "${code}" copied to clipboard`,
    });
    window.open(`https://${storeDomain}`, '_blank');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setShowSuggestions(false);
      window.location.href = `/search?q=${encodeURIComponent(searchQuery)}`;
    }
  };

  const handleSearchInputChange = async (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    if (value.trim().length >= 2) {
      try {
        const suggestions = await getSearchSuggestions(value);
        setSearchSuggestions(suggestions);
        setShowSuggestions(true);
      } catch (err) {
        console.error('Error fetching suggestions:', err);
      }
    } else {
      setSearchSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (storeName) => {
    setSearchQuery(storeName);
    setShowSuggestions(false);
    window.location.href = `/search?q=${encodeURIComponent(storeName)}`;
  };

  // Loading skeleton component
  const LoadingSkeleton = () => (
    <div className="animate-pulse">
      <div className="h-8 bg-slate-200 rounded w-48 mb-6"></div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="bg-white rounded-lg p-6">
            <div className="w-20 h-20 bg-slate-200 rounded-xl mx-auto mb-4"></div>
            <div className="h-4 bg-slate-200 rounded w-24 mx-auto mb-2"></div>
            <div className="h-6 bg-slate-200 rounded w-16 mx-auto"></div>
          </div>
        ))}
      </div>
    </div>
  );

  const TrendingLoadingSkeleton = () => (
    <div className="animate-pulse">
      <div className="h-8 bg-slate-200 rounded w-64 mb-6"></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="bg-white rounded-lg p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-slate-200 rounded-lg"></div>
                <div>
                  <div className="h-4 bg-slate-200 rounded w-24 mb-2"></div>
                  <div className="h-3 bg-slate-200 rounded w-16"></div>
                </div>
              </div>
              <div className="h-6 bg-slate-200 rounded w-16"></div>
            </div>
            <div className="h-4 bg-slate-200 rounded w-full mb-2"></div>
            <div className="h-3 bg-slate-200 rounded w-3/4 mb-4"></div>
            <div className="h-10 bg-slate-200 rounded w-full"></div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50">
      <Header />

      {/* Hero Section */}
      <section className="py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-4">
            <span className="block text-slate-900">
              Peterhahn Fashion Coupon Club
            </span>
            <span className="block text-2xl sm:text-3xl md:text-4xl mt-2">Find the Best Coupon Codes</span>
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-slate-600 mb-6 sm:mb-8 max-w-2xl mx-auto px-4">
            Save big with verified promo codes from thousands of stores. Never pay full price again!
          </p>
          
          {/* Search Bar */}
          <form onSubmit={handleSearch} className="max-w-2xl mx-auto mb-8">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Search for stores or coupons..."
                value={searchQuery}
                onChange={handleSearchInputChange}
                onFocus={() => searchSuggestions.length > 0 && setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                className="pl-12 pr-4 py-6 text-lg border-2 border-slate-200 focus:border-slate-900 rounded-2xl shadow-lg"
                autoComplete="off"
              />
              
              {/* Search Suggestions Dropdown */}
              {showSuggestions && searchSuggestions.length > 0 && (
                <div className="absolute top-full mt-2 w-full bg-white border-2 border-slate-200 rounded-2xl shadow-xl z-50">
                  {searchSuggestions.map((store) => (
                    <div
                      key={store.id}
                      onClick={() => handleSuggestionClick(store.name)}
                      className="flex items-center space-x-3 p-4 hover:bg-slate-100 cursor-pointer transition-colors first:rounded-t-2xl last:rounded-b-2xl"
                    >
                      <img
                        src={store.logo}
                        alt={store.name}
                        className="w-8 h-8 object-contain rounded"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(store.name)}&background=1e293b&color=fff&size=64`;
                        }}
                      />
                      <span className="font-medium text-slate-900">{store.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </form>

          {/* Trending Searches */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            <span className="text-sm text-slate-600 mr-2">Trending:</span>
            {trendingSearches.slice(0, 6).map((search, index) => (
              <Link
                key={index}
                to={`/search?q=${encodeURIComponent(search)}`}
                className="text-sm text-slate-700 hover:text-slate-900 hover:underline"
              >
                {search}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Error State */}
      {error && !loading && (
        <section className="py-8 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto text-center">
            <Card className="p-8">
              <CardContent>
                <p className="text-slate-600 mb-4">Unable to load coupons. Please try again.</p>
                <Button onClick={loadData} className="bg-slate-900 hover:bg-slate-800 text-white">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Retry
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* Loading State */}
      {loading && (
        <>
          <section className="py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-center mb-6">
                <Loader2 className="w-6 h-6 animate-spin text-slate-900 mr-2" />
                <span className="text-slate-600">Loading coupons...</span>
              </div>
              <LoadingSkeleton />
            </div>
          </section>
          <section className="py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
              <TrendingLoadingSkeleton />
            </div>
          </section>
        </>
      )}

      {/* Featured Stores - Only show when loaded */}
      {!loading && !error && featuredStores.length > 0 && (
        <section className="py-8 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center">
              <Star className="w-8 h-8 mr-2 text-slate-900" />
              Today's Top Coupons
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4">
              {featuredStores.map(store => {
                const storeCoupon = featuredCoupons.find(c => c.store_id === store.id);
                return (
                  <Link key={store.id} to={`/store/${store.id}`}>
                    <Card className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-2 border-transparent hover:border-slate-300 overflow-hidden group">
                      <CardContent className="p-6 text-center">
                        <div className="w-20 h-20 mx-auto mb-4 rounded-xl bg-white shadow-md flex items-center justify-center group-hover:shadow-lg transition-shadow">
                          <img 
                            src={store.logo} 
                            alt={store.name} 
                            className="w-16 h-16 object-contain" 
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(store.name)}&background=1e293b&color=fff&size=128`;
                            }} 
                          />
                        </div>
                        <h3 className="font-semibold text-slate-900 mb-2">{store.name}</h3>
                        {storeCoupon && (
                          <Badge className="bg-slate-900 text-white">
                            {storeCoupon.discount}
                          </Badge>
                        )}
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Trending Coupons - Only show when loaded */}
      {!loading && !error && trendingCoupons.length > 0 && (
        <section className="py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center">
              <TrendingUp className="w-8 h-8 mr-2 text-slate-900" />
              Today's Trending Coupons
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {trendingCoupons.map(coupon => {
                const storeInfo = { name: coupon.store_name, domain: coupon.store_domain, logo: coupon.logo || `https://ui-avatars.com/api/?name=${encodeURIComponent(coupon.store_name)}&background=1e293b&color=fff&size=128` };
                return (
                  <Card key={coupon.id} className="hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-slate-300">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <img 
                            src={storeInfo.logo} 
                            alt={storeInfo.name} 
                            className="w-12 h-12 rounded-lg object-contain" 
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(storeInfo.name || 'Store')}&background=1e293b&color=fff&size=128`;
                            }} 
                          />
                          <div>
                            <h3 className="font-semibold text-slate-900">{storeInfo.name}</h3>
                            {coupon.verified && (
                              <Badge variant="outline" className="text-xs border-green-500 text-green-700">
                                Verified
                              </Badge>
                            )}
                          </div>
                        </div>
                        <Badge className="bg-slate-900 text-white font-bold">
                          {coupon.discount}
                        </Badge>
                      </div>
                      <h4 className="font-semibold text-slate-900 mb-2">{coupon.title}</h4>
                      <p className="text-sm text-slate-600 mb-4 line-clamp-2">{coupon.description}</p>
                      <div className="flex items-center justify-between">
                        {coupon.code ? (
                          <Button
                            onClick={() => handleCopyCode(coupon.code, coupon.id, storeInfo.domain)}
                            className="flex-1 bg-slate-900 hover:bg-slate-800 text-white"
                          >
                            {copiedCode === coupon.id ? (
                              <><Check className="w-4 h-4 mr-2" /> Copied!</>
                            ) : (
                              <><Copy className="w-4 h-4 mr-2" /> {coupon.code}</>
                            )}
                          </Button>
                        ) : (
                          <Button 
                            onClick={() => {
                              toast({
                                title: "Opening Deal!",
                                description: `Redirecting to ${storeInfo.name} for this exclusive deal`,
                              });
                              window.open(`https://${storeInfo.domain}`, '_blank');
                            }}
                            className="flex-1 bg-slate-900 hover:bg-slate-800 text-white"
                          >
                            Get Deal
                          </Button>
                        )}
                      </div>
                      {coupon.savings && (
                        <p className="text-xs text-green-600 mt-2 text-center font-medium">
                          A user just saved {coupon.savings}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Popular Stores Grid - Only show when loaded */}
      {!loading && !error && stores.length > 0 && (
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white/50">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">Popular Stores</h2>
            <div className="grid grid-cols-3 md:grid-cols-6 lg:grid-cols-8 gap-4">
              {stores.map(store => (
                <Link key={store.id} to={`/store/${store.id}`}>
                  <Card className="hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer">
                    <CardContent className="p-4 text-center">
                      <img 
                        src={store.logo} 
                        alt={store.name} 
                        className="w-12 h-12 mx-auto mb-2 rounded object-contain" 
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(store.name || 'Store')}&background=1e293b&color=fff&size=128`;
                        }} 
                      />
                      <p className="text-xs font-medium text-slate-900 truncate">{store.name}</p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default HomePage;
